/* istanbul ignore file */
import { ListFiltersLayout as FiltersLayout, StaticFilters as Filters } from 'framework/list';

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PersonTimeline from 'domain/shared/timeline/person';
import { useEmployeeService, CREATE_EMPLOYEE_OFFER, useEmployeeUrl } from 'domain/shared/employee';
import { useEmployeeId } from 'domain/people/employee/employeeIdentityRouting';
import useEmployeeNoteItem from 'domain/people/employee/Timeline/note/employeeNoteRegistry';
import useEmployeeFeedbackItem from 'domain/people/employee/Timeline/feedback/employeeFeedbackRegistry';
import useEmployeeHealthcheckItem from 'domain/people/employee/Timeline/healthcheck/employeeHealthcheckRegistry';
import EmployeeTimelineFeedbackFlagTypeFilter from 'domain/people/employee/Timeline/shared/filter/EmployeeTimelineFeedbackFlagTypeFilter';
import { EMPLOYEE_TIMELINE_RECORDS_ALIAS } from 'domain/people/employee/Timeline/shared/input/EmployeeTimelineRecordTypeDropdown';
import EmployeeTimelineNoteTagFilter from './shared/filter/EmployeeTimelineNoteTagFilter';
import { CreateButton } from 'framework/ui/ui-builder';
import { useEmployeeOfferUrl } from 'domain/people/employee/offers/employeeOfferRouting';
import EditNote from '@mui/icons-material/EditNote';
import Feedback from '@mui/icons-material/Feedback';
import EmployeeTimelineRecordTypeFilter from './shared/filter/EmployeeTimelineRecordTypeFilter';
import { TimelineRegistry as EmployeeTimelineRegistry } from './types';
import AddTimelineItemsButtons from 'domain/shared/timeline/person/AddTimelineItemsButtons';
import { useTimelineRefresh } from 'domain/people/employee/dashboard/List/EmployeeDashboardItem/TimelineRefreshContext';
import EmployeeTimelineHtmlTagDependencies from './shared/EmployeeTimelineHtmlTagDependencies';
import type { TimelineRegistry } from 'framework/ui/timeline';

const oldHashEntry = '#entry';
const FEEDBACK_TYPE_SOURCE = 'feedback.inappropriateContentType';
const NOTE_TAG_SOURCE = 'note.tag';
const EMPLOYEE_TIMELINE_MESSAGE_CONTEXT = 'EMPLOYEE_TIMELINE_MESSAGE_CONTEXT';

const formConfig = {
  CreateNoteForm: {
    icon: <EditNote />,
    text: 'New note',
  },
  CreateFeedbackForm: {
    icon: <Feedback />,
    text: 'New feedback',
  },
  CreateHealthcheckForm: {
    icon: <EditNote />,
    text: 'New healthcheck',
  },
  default: {
    icon: <EditNote />,
    text: 'New entry',
  },
};

const EmployeeTimelinePage = () => {
  const employeeId = useEmployeeId() as string;
  const { searchTimelineItems, createTimelineItem, getTimelinePageById, pinEmployeeTimeline } = useEmployeeService();
  const noteRegistry = useEmployeeNoteItem();
  const feedbackRegistry = useEmployeeFeedbackItem();
  const healthcheckRegistry = useEmployeeHealthcheckItem();
  const { getTimelineSingleEntityUrl } = useEmployeeUrl();
  const navigate = useNavigate();
  const location = useLocation();
  const { getCreateUrl } = useEmployeeOfferUrl();
  const { refreshTimeline } = useTimelineRefresh();
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    refreshTimeline.current = () => {
      setRefreshKey(prev => prev + 1);
    };
  }, [refreshTimeline]);

  useEffect(() => {
    const { hash } = location;
    const entryId = hash && hash.includes(oldHashEntry) && hash.replace(oldHashEntry, '');

    if (entryId) {
      navigate(getTimelineSingleEntityUrl(employeeId!, entryId), { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const registries: EmployeeTimelineRegistry[] = [noteRegistry, feedbackRegistry, healthcheckRegistry];

  const TimelineItemInput = (props: any) => <AddTimelineItemsButtons {...props} registries={registries} />;

  const filters = (
    <Filters>
      <FiltersLayout>
        <EmployeeTimelineRecordTypeFilter
          label="Entity"
          source="type"
          cleanableFilters={[FEEDBACK_TYPE_SOURCE, NOTE_TAG_SOURCE]}
        />
        <EmployeeTimelineFeedbackFlagTypeFilter
          disabled={({ type = {} }: any) => !type.in || type.in[0] !== EMPLOYEE_TIMELINE_RECORDS_ALIAS.FEEDBACK_CREATED}
          source={FEEDBACK_TYPE_SOURCE}
          label="Flagged entries"
          labelInDropdown="Inappropriate flag (feedbacks only)"
        />
        <EmployeeTimelineNoteTagFilter
          disabled={({ type = {} }: any) => !type.in || type.in[0] !== EMPLOYEE_TIMELINE_RECORDS_ALIAS.NOTE_CREATED}
          source={NOTE_TAG_SOURCE}
          label="Tag"
          labelInDropdown="Tag (notes only)"
        />
      </FiltersLayout>
    </Filters>
  );

  return (
    <EmployeeTimelineHtmlTagDependencies employeeId={employeeId}>
      <PersonTimeline
        key={refreshKey}
        entityId={employeeId!}
        timelineEntryIdSource="*" // TMP solution
        registries={registries as TimelineRegistry[]}
        getTimelineItems={searchTimelineItems}
        createTimelineItem={createTimelineItem}
        getTimelinePageById={getTimelinePageById}
        pinTimelineAction={pinEmployeeTimeline}
        filter={filters}
        messageContext={EMPLOYEE_TIMELINE_MESSAGE_CONTEXT}
        formConfig={formConfig}
        AddTimelineItemInput={TimelineItemInput}
        additionalButtons={[
          <CreateButton
            url={getCreateUrl}
            label="Offer"
            key="add-offer"
            permissionToCheck={CREATE_EMPLOYEE_OFFER}
            variant="contained"
          />,
        ]}
        noEntriesMessage="No entries in this worker timeline yet."
      />
    </EmployeeTimelineHtmlTagDependencies>
  );
};

export default EmployeeTimelinePage;
